import React, { useState, useEffect, useMemo } from 'react';
import { ScanLine, Search, Download, Save, RefreshCw, Edit, Trash2, AlertTriangle, X, CheckCircle, List } from 'lucide-react';
import MainService from '../../services/MainService';
import ClientListGrid, { useClientListGrid, exportClientListExcel } from '../ui/ClientListGrid';

const LIST_TAKE = 10000;

const IN_SCAN_COLUMNS = [
  { field: 'voucher', title: 'Voucher No', widthClass: 'w-[20%]', hideable: true },
  { field: 'date', title: 'Date', widthClass: 'w-[18%]', hideable: true },
  { field: 'employee', title: 'Driver', widthClass: 'w-[25%]', hideable: true },
  { field: 'createdby', title: 'Created By', widthClass: 'w-[20%]', hideable: true },
  { field: 'actions', title: 'Options', widthClass: 'w-[17%]', hideable: true, sortable: false, filterable: false, groupable: false, align: 'center' },
];

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateString;
  }
};

const getCellValue = (row, field) => {
  switch (field) {
    case 'voucher':
      return row.VoucherNo || '';
    case 'date':
      return formatDate(row.Date);
    case 'employee':
      return row.EmployeeName || '-';
    case 'createdby':
      return row.UserName || '-';
    default:
      return row[field];
  }
};

const InScan = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    VoucherNo: '',
    EmployeeId: '',
  });

  const filteredRows = useMemo(() => {
    const term = (searchTerm || '').toLowerCase().trim();
    if (!term) return rows;
    return rows.filter((row) =>
      (row.VoucherNo || '').toLowerCase().includes(term) ||
      (row.EmployeeName || '').toLowerCase().includes(term) ||
      (row.UserName || '').toLowerCase().includes(term)
    );
  }, [rows, searchTerm]);

  const columns = useMemo(() => IN_SCAN_COLUMNS, []);
  const grid = useClientListGrid({ columns, rows: filteredRows, getCellValue });

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    loadRows();
  }, []);

  // Toast notification effect
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadEmployees = async () => {
    try {
      const response = await MainService.DriversInDropDown();
      setEmployees(response || []);
    } catch (err) {
      console.error('Load employees error:', err);
    }
  };

  const loadRows = async () => {
    try {
      setLoading(true);
      const response = await MainService.StoresInList({
        Type: 'STORE IN',
        Keyword: '',
        Skip: 0,
        Take: LIST_TAKE,
      });
      const list = (response?.Data || response || []).map((x, i) => ({
        id: x.Id || i + 1,
        VoucherNo: x.VoucherNo || '',
        Date: x.Date || '',
        EmployeeName: x.Employee?.Name || '',
        EmployeeId: x.EmployeeId || x.Employee?.Id || '',
        UserName: x.User?.UserName || '',
        CreatedBy: x.User?.UserName || '',
      }));
      setRows(list);
    } catch (err) {
      console.error('Load store in error:', err);
      setError('Failed to load store in records');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ VoucherNo: '', EmployeeId: '' });
    setEditing(null);
  };

  const handleRefresh = async () => {
    resetForm();
    setSearchTerm('');
    setError('');
    setSuccess('');
    await loadRows();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.VoucherNo || formData.VoucherNo === '0') {
      setError('Enter Voucher No');
      return;
    }
    try {
      setLoading(true);
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const payload = {
        VoucherNo: formData.VoucherNo.trim(),
        EmployeeId: formData.EmployeeId || null,
        Type: 'STORE IN',
        UserId: userInfo.Id || null,
      };
      if (editing?.id) payload.Id = editing.id;
      const resp = await MainService.SaveStore(payload);
      if (resp === true || resp === 'true' || resp === 'Success') {
        setSuccess(editing ? 'Store in updated successfully' : 'Store in created successfully');
        await loadRows();
        resetForm();
      } else {
        setError(resp || 'Failed to save store in');
      }
    } catch (err) {
      console.error('Save store in error:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditing(row);
    setFormData({
      VoucherNo: row.VoucherNo || '',
      EmployeeId: row.EmployeeId || '',
    });
    setError('');
    setSuccess('');
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      setLoading(true);
      setDeleteDialogOpen(false);
      const resp = await MainService.DeleteStore(rowToDelete.id);
      if (resp === 'Success' || resp === true || resp === 'true') {
        setSuccess('Store in deleted successfully');
        await loadRows();
      } else {
        setError(resp || 'Failed to delete store in');
      }
    } catch (err) {
      console.error('Delete store in error:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
      setRowToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleExportExcel = () => {
    try {
      exportClientListExcel({
        sheetName: 'In Scan',
        fileName: `store_in_${new Date().toISOString().split('T')[0]}.xlsx`,
        columns,
        groups: grid.displayGroups,
        groupField: grid.groupField,
        getCellValue,
      });
      setSuccess('Store in data exported successfully');
    } catch (err) {
      setError(err?.message === 'No rows to export' ? 'No data to export' : 'Failed to export data');
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Form Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center mb-2">
            <ScanLine className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">In Scan</h1>
          </div>
          <p className="text-sm text-gray-500 ml-8">Manage in scan records</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-12 gap-4 items-end">
            <div className="col-span-3">
              <label htmlFor="VoucherNo" className="block text-sm font-medium text-gray-700 mb-2">
                Voucher Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="VoucherNo"
                name="VoucherNo"
                value={formData.VoucherNo}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent"
                placeholder="Enter voucher number"
              />
            </div>

            <div className="col-span-3">
              <label htmlFor="EmployeeId" className="block text-sm font-medium text-gray-700 mb-2">
                Driver
              </label>
              <select
                id="EmployeeId"
                name="EmployeeId"
                value={formData.EmployeeId}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent"
              >
                <option value="">Select Driver</option>
                {employees.map((emp) => (
                  <option key={emp.Id} value={emp.Id}>
                    {emp.Name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-4 flex items-center gap-2">
              <button
                type="button"
                onClick={handleRefresh}
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                type="submit"
                disabled={loading}
                className="p-2 bg-primary text-white rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={editing ? 'Update' : 'Save'}
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <List className="w-5 h-5 text-primary mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">In Scan List</h3>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-8 pr-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent w-64"
              />
            </div>
            <button
              onClick={handleExportExcel}
              className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
              title="Export Excel"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>
        </div>

        <ClientListGrid
          columns={columns}
          grid={grid}
          loading={loading}
          loadingText="Loading..."
          emptyText="No store in records found"
          minWidth="900px"
          rowKey={(row) => row.id}
          renderCell={(row, column) => {
            if (column.field === 'actions') {
              return (
                <div className="flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(row);
                    }}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(row);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            }
            return getCellValue(row, column.field);
          }}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="bg-red-50 border-b border-red-200 px-6 py-4 rounded-t-lg">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-red-900">Confirm Delete</h3>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete this store in record?
              </p>
              <p className="text-sm text-gray-500">
                This action cannot be undone and will permanently remove the record from the system.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Confirm
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InScan;
